import { ICalendarItemData } from 'Calendar/types';
import { mappingResToCalendarDataItem } from 'services/helpers';
import { IBodyApiQuickBooking } from 'services/types/quickBooking';
import { IPayloadBookingV2 } from 'services/types/request';
import { IAppointmentResItem } from 'services/types/response';
import moment from 'utils/moment';
import uuid from 'utils/uuid';

/**
 * Draft appointments: the card the calendar shows the instant BOOK is pressed, before the
 * server answers (booking-enhance shops only).
 *
 * Every booking item gets a `clientRequestId`; the draft's calendar id is `draft:<that id>`.
 * The server echoes the id on the created appointment, so the reducer can swap the draft for
 * the real card in place. Drafts live only in the store: a reload drops them and the day
 * reload brings the real appointment back.
 */

export const DRAFT_ID_PREFIX = 'draft:';
export const DRAFT_TIMEOUT_MS = 15_000;

export const draftIdOf = (clientRequestId: string) => DRAFT_ID_PREFIX + clientRequestId;
export const isDraftId = (id: string | null | undefined) => !!id && id.startsWith(DRAFT_ID_PREFIX);

const BOOKING_TIME_FORMAT = 'MM/DD/YYYY HH:mm';
/** The format `mappingResToCalendarDataItem` recognises for `start_time` / `end_time`. */
const RES_TIME_FORMAT = 'MM/DD/YYYY HH:mm A';

type IDraftSource = {
  clientRequestId: string;
  staffId: number;
  startTime: string;
  endTime: string;
  customerId: number | string;
  customerName: string;
  services: { id: number; serviceName: string; duration: number }[];
  note: string;
  requestStaff: boolean;
  isAnybody: number;
  bookingType: number;
};

/** Resolves a customer name for the draft card; the sagas build it from the store's customer list. */
export type ICustomerNameLookup = (customerId: number | string | undefined) => string | undefined;

const toCalendarItem = (src: IDraftSource): ICalendarItemData => {
  const start = moment(src.startTime, BOOKING_TIME_FORMAT);
  const end = moment(src.endTime, BOOKING_TIME_FORMAT);
  // Shape of a booking response item, so the draft goes through the same mapping as a real one.
  const res = {
    id: draftIdOf(src.clientRequestId),
    customerId: src.customerId,
    customerName: src.customerName,
    customerPhone: '',
    staffId: src.staffId,
    services: src.services.map(s => ({ id: s.id, serviceName: s.serviceName, duration: s.duration })),
    bookingType: src.bookingType,
    note: src.note,
    status: 0,
    isAnybody: src.isAnybody,
    requestStaff: src.requestStaff,
    strStatus: 'NEW',
    start_time: start.format(RES_TIME_FORMAT),
    end_time: end.format(RES_TIME_FORMAT),
  } as unknown as IAppointmentResItem;
  const item = mappingResToCalendarDataItem(res);
  item.data.draft = true;
  return item;
};

/**
 * Stamps a `clientRequestId` on every item of a Book Appointment payload and returns the
 * drafts to show for them. The returned payload is what must be sent to the server.
 */
export const prepareBookingV2Drafts = (
  payload: IPayloadBookingV2,
  serviceNamesById: Record<string, { name: string; duration: number }>,
  customerName: ICustomerNameLookup,
): { payload: IPayloadBookingV2; drafts: ICalendarItemData[]; draftIds: string[] } => {
  const items = payload.items.map(item => ({ ...item, clientRequestId: item.clientRequestId || uuid() }));
  const drafts = items.map(item => toCalendarItem({
    clientRequestId: item.clientRequestId as string,
    staffId: item.staffId,
    startTime: item.startTime,
    endTime: item.endTime,
    customerId: payload.customerId,
    customerName: payload.customerName || customerName(payload.customerId) || 'Walk-In',
    services: item.serviceIds.map(id => ({
      id,
      serviceName: serviceNamesById[id]?.name || '',
      duration: serviceNamesById[id]?.duration || 0,
    })),
    note: payload.note,
    requestStaff: item.requestStaff ?? payload.requestStaff,
    isAnybody: payload.isAnybody,
    bookingType: payload.bookingType,
  }));
  return { payload: { ...payload, items }, drafts, draftIds: drafts.map(d => d.id) };
};

/** Same for a Quick Booking / Caller v2 payload (one request item = one appointment). */
export const prepareQuickBookingDrafts = (
  items: IBodyApiQuickBooking[],
  customerName: ICustomerNameLookup,
): { items: IBodyApiQuickBooking[]; drafts: ICalendarItemData[]; draftIds: string[] } => {
  const stamped = items.map(item => ({ ...item, clientRequestId: item.clientRequestId || uuid() }));
  const drafts = stamped.map(item => {
    const start = moment(item.startTime, BOOKING_TIME_FORMAT);
    const duration = item.duration || 15;
    return toCalendarItem({
      clientRequestId: item.clientRequestId as string,
      staffId: item.staffId,
      startTime: item.startTime,
      endTime: start.clone().add(duration, 'minute').format(BOOKING_TIME_FORMAT),
      customerId: item.customerId,
      customerName: customerName(item.customerId) || 'Walk-In',
      services: item.services.map(s => ({ id: s.id, serviceName: s.serviceName, duration: 0 })),
      note: item.note,
      requestStaff: item.requestStaff,
      isAnybody: item.isAnybody,
      bookingType: item.bookingType,
    });
  });
  return { items: stamped, drafts, draftIds: drafts.map(d => d.id) };
};
